import type {
  Bin,
  BinCreate,
  BinDetail,
  BinRow,
  BinUpdate,
  CommandResult,
  ListResponse,
  LotDetail,
  LotRow,
  MoveGoodsPayload,
  MoveGoodsResult,
  MovePalletPayload,
  MovePalletResult,
  PalletDetail,
  PalletRow,
  ShelfCreate,
  ShelfUpdate,
  StockByArticleRow,
  StockMovementRow,
  StockRow,
  Stocktake,
  StocktakeCountPayload,
  StocktakeCountResult,
  StocktakeCreate,
  StocktakeLine,
  StocktakePostPayload,
  StocktakePostResult,
  WarehouseArea,
  WarehouseAreaCreate,
  WarehouseAreaUpdate,
  WarehouseMap,
} from '@warehouse/shared';

import { api } from '../../lib/api-client';
import { listQueryParams, type ListState } from '../../lib/list-query';

/**
 * The warehouse-core API surface, in one place.
 *
 * Every list goes through `listQueryParams` so search / filters / sort / paging
 * are serialized identically to every other list in the app, and every mutation
 * hits an endpoint that wraps a CommandBus command — so a retried POST is a
 * no-op, not a second pallet move.
 */

const BASE = '/warehouse';

// --- map ---------------------------------------------------------------------

export const warehouseApi = {
  map: () => api.get<WarehouseMap>(`${BASE}/map`),

  listAreas: () => api.get<WarehouseArea[]>(`${BASE}/areas`),
  createArea: (body: WarehouseAreaCreate) => api.post<WarehouseArea>(`${BASE}/areas`, body),
  updateArea: (id: string, body: WarehouseAreaUpdate) =>
    api.patch<WarehouseArea>(`${BASE}/areas/${id}`, body),
  deleteArea: (id: string) => api.delete<void>(`${BASE}/areas/${id}`),

  createShelf: (body: ShelfCreate) =>
    api.post<{ shelfId: string; binsCreated: number }>(`${BASE}/shelves`, body),
  updateShelf: (id: string, body: ShelfUpdate) =>
    api.patch<{ shelfId: string; renamed: number; created: number; removed: number }>(
      `${BASE}/shelves/${id}`,
      body,
    ),
  deleteShelf: (id: string) => api.delete<void>(`${BASE}/shelves/${id}`),

  // --- bins ------------------------------------------------------------------

  listBins: (state: ListState) =>
    api.get<ListResponse<BinRow>>(`${BASE}/bins`, { query: listQueryParams(state) }),
  getBin: (id: string) => api.get<BinDetail>(`${BASE}/bins/${id}`),
  lookupBin: (code: string) => api.get<Bin>(`${BASE}/bins/lookup/${encodeURIComponent(code)}`),
  createBin: (body: BinCreate) => api.post<Bin>(`${BASE}/bins`, body),
  updateBin: (id: string, body: BinUpdate) => api.patch<Bin>(`${BASE}/bins/${id}`, body),
  deleteBin: (id: string) => api.delete<void>(`${BASE}/bins/${id}`),

  // --- stock -----------------------------------------------------------------

  listStock: (state: ListState) =>
    api.get<ListResponse<StockRow>>(`${BASE}/stock`, { query: listQueryParams(state) }),
  listStockByArticle: (state: ListState) =>
    api.get<ListResponse<StockByArticleRow>>(`${BASE}/stock/by-article`, {
      query: listQueryParams(state),
    }),
  listMovements: (state: ListState) =>
    api.get<ListResponse<StockMovementRow>>(`${BASE}/movements`, { query: listQueryParams(state) }),

  // --- pallets / lots --------------------------------------------------------

  listPallets: (state: ListState) =>
    api.get<ListResponse<PalletRow>>(`${BASE}/pallets`, { query: listQueryParams(state) }),
  getPallet: (id: string) => api.get<PalletDetail>(`${BASE}/pallets/${id}`),
  lookupPallet: (code: string) =>
    api.get<PalletDetail>(`${BASE}/pallets/lookup/${encodeURIComponent(code)}`),
  listLots: (state: ListState) =>
    api.get<ListResponse<LotRow>>(`${BASE}/lots`, { query: listQueryParams(state) }),
  getLot: (id: string) => api.get<LotDetail>(`${BASE}/lots/${id}`),

  // --- moves -----------------------------------------------------------------

  movePallet: (body: MovePalletPayload) =>
    api.post<CommandResult<MovePalletResult>>(`${BASE}/moves/pallet`, body),
  moveGoods: (body: MoveGoodsPayload) =>
    api.post<CommandResult<MoveGoodsResult>>(`${BASE}/moves/goods`, body),

  // --- stocktaking -----------------------------------------------------------

  listStocktakes: (state: ListState) =>
    api.get<ListResponse<Stocktake>>(`${BASE}/stocktakes`, { query: listQueryParams(state) }),
  getStocktake: (id: string) => api.get<Stocktake>(`${BASE}/stocktakes/${id}`),
  createStocktake: (body: StocktakeCreate) => api.post<Stocktake>(`${BASE}/stocktakes`, body),
  deleteStocktake: (id: string) => api.delete<void>(`${BASE}/stocktakes/${id}`),
  listStocktakeLines: (id: string, state: ListState) =>
    api.get<ListResponse<StocktakeLine>>(`${BASE}/stocktakes/${id}/lines`, {
      query: listQueryParams(state),
    }),
  listStocktakeBins: (id: string) =>
    api.get<StocktakeBinProgress[]>(`${BASE}/stocktakes/${id}/bins`),
  startStocktake: (id: string) => api.post<Stocktake>(`${BASE}/stocktakes/${id}/start`, {}),
  reviewStocktake: (id: string) => api.post<Stocktake>(`${BASE}/stocktakes/${id}/review`, {}),
  postStocktake: (id: string, body: StocktakePostPayload) =>
    api.post<StocktakePostResult>(`${BASE}/stocktakes/${id}/post`, body),
  count: (body: StocktakeCountPayload) =>
    api.post<CommandResult<StocktakeCountResult>>(`${BASE}/stocktakes/count`, body),
} as const;

export interface StocktakeBinProgress {
  binId: string;
  binCode: string;
  areaCode: string;
  lineCount: number;
  countedCount: number;
}

/** Query keys — one namespace so `useLiveQuery` can invalidate a whole area. */
export const warehouseKeys = {
  all: ['warehouse'] as const,
  map: () => ['warehouse', 'map'] as const,
  areas: () => ['warehouse', 'areas'] as const,
  bins: (state?: unknown) => ['warehouse', 'bins', state] as const,
  bin: (id: string) => ['warehouse', 'bin', id] as const,
  stock: (state?: unknown) => ['warehouse', 'stock', state] as const,
  stockByArticle: (state?: unknown) => ['warehouse', 'stock-by-article', state] as const,
  movements: (state?: unknown) => ['warehouse', 'movements', state] as const,
  pallets: (state?: unknown) => ['warehouse', 'pallets', state] as const,
  pallet: (id: string) => ['warehouse', 'pallet', id] as const,
  lots: (state?: unknown) => ['warehouse', 'lots', state] as const,
  lot: (id: string) => ['warehouse', 'lot', id] as const,
  stocktakes: (state?: unknown) => ['warehouse', 'stocktakes', state] as const,
  stocktake: (id: string) => ['warehouse', 'stocktake', id] as const,
  stocktakeLines: (id: string, state?: unknown) =>
    ['warehouse', 'stocktake-lines', id, state] as const,
  stocktakeBins: (id: string) => ['warehouse', 'stocktake-bins', id] as const,
} as const;
